import React, { useMemo, useState } from 'react';
import Modal from '../shared/Modal';
import { SelectField } from '../shared/FormField';
import ModuleScopeField, { parseModuleScopeValue } from '../shared/ModuleScopeField';

const blank = {
  user_uuid:        '',
  role_code:        '',
  institution_uuid: '',
  module_scope:     '',
};

const parseError = (error) =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  'Unable to create role assignment.';

const formatRoleLabel = (role = {}) => {
  const parts = [role.name, role.code].filter(Boolean);
  return parts.length ? parts.join(' - ') : role.uuid;
};

export default function CreateRoleAssignmentModal({
  isOpen,
  onClose,
  onCreate,
  users = [],
  roles = [],
  institutions = [],
}) {
  const [form, setForm] = useState(blank);
  const [status, setStatus] = useState('idle');
  const [errMsg, setErrMsg] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const isR03 = form.role_code === 'domain_data_steward';

  const userOptions = useMemo(
    () =>
      users.map((u) => ({
        value: u.uuid ?? u.id,
        label: u.name ? `${u.name}${u.email ? ` (${u.email})` : ''}` : (u.email || u.uuid || u.id || 'Unknown user'),
      })),
    [users]
  );

  const roleOptions = useMemo(
    () =>
      roles
        .slice()
        .sort((a, b) => String(a.name ?? a.code ?? '').localeCompare(String(b.name ?? b.code ?? ''), undefined, { numeric: true, sensitivity: 'base' }))
        .map((role) => ({
          value: role.code,
          label: formatRoleLabel(role),
        })),
    [roles]
  );

  const institutionOptions = useMemo(
    () => [
      { value: '', label: 'All Institutions' },
      ...institutions.map((i) => ({
        value: i.uuid ?? i.id,
        label: i.name ?? i.uuid ?? i.id ?? 'Unknown institution',
      })),
    ],
    [institutions]
  );

  const submit = async () => {
    if (submitting) return;
    if (!form.user_uuid || !form.role_code) return;
    if (isR03 && parseModuleScopeValue(form.module_scope).length === 0) {
      setErrMsg('R03 assignments need at least one module. Use Module 1 and 2 for the current handoff.');
      setStatus('error');
      return;
    }
    setSubmitting(true);
    setStatus('idle');
    setErrMsg('');
    try {
      await onCreate({
        user_uuid:        form.user_uuid,
        role_code:        form.role_code,
        institution_uuid: form.institution_uuid || null,
        module_scope:     form.module_scope || null,
      });
      setStatus('success');
    } catch (error) {
      setErrMsg(parseError(error));
      setStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setForm(blank);
    setStatus('idle');
    setErrMsg('');
    onClose();
  };

  return (
    <Modal
      title="Assign User Role"
      isOpen={isOpen}
      onClose={handleClose}
      onSubmit={submit}
      submitLabel="Create"
      status={status}
      errorMessage={errMsg}
      onContinue={handleClose}
      successTitle="Role Assignment Created"
      successMessage="The assignment was saved to the backend."
      isSubmitting={submitting}
    >
      <SelectField
        label="User"
        required
        value={form.user_uuid}
        onChange={set('user_uuid')}
        options={userOptions}
        placeholder="Select User"
      />
      <SelectField
        label="Role"
        required
        value={form.role_code}
        onChange={set('role_code')}
        options={roleOptions}
        placeholder="Select Role"
      />
      <SelectField
        label="Institution Scope"
        value={form.institution_uuid}
        onChange={set('institution_uuid')}
        options={institutionOptions}
        placeholder="All Institutions"
      />
      <ModuleScopeField
        required={isR03}
        value={form.module_scope}
        onChange={set('module_scope')}
        showShortcut={isR03}
        note="Select one or more modules, or use All Modules to apply the full set at once."
      />
      <div className="rounded-xl border border-gray-100 bg-gray-50 px-4 py-3 text-xs text-gray-600">
        Expiration is calculated by the backend, so there is no expiry input here.
      </div>
    </Modal>
  );
}
